import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '.';
import ImageManager from '../core/image-manager';
import { TfileIdToUrlMap, TFileId } from '../types/entity/image.type';
import { TImageElementConfig } from '../types/entity/element.type';
import { CustomError } from '../lib/error';
import { eventBus } from '../core/event-bus';
import logger from 'src/logger';
import { miniSerializeErrorOverride } from './serialize-error';
import { getSessionIdentifier } from 'src/helpers/session.helpers';

export const uploadImageAction = createAsyncThunk(
  'uploadImage/post',
  async ({ file, onProgress }: { file: File; onProgress?: (perc: number) => void }, { getState, dispatch }) => {
    const session = (getState() as RootState).session;
    try {
      if (!session) throw new CustomError({ errorCode: 'SessionNotFound' });
      const { fileId, fileUrl } = await ImageManager.uploadImage(file, { ...getSessionIdentifier(session), onProgress });
      dispatch(updateFileDownloadUrls({ [fileId]: fileUrl }));
      dispatch(addFileId(fileId));
      return fileId;
    } catch (err) {
      logger.reportError(err, { message: 'Error in uploadImageAction' });
      throw err;
    }
  },
  {
    serializeError: miniSerializeErrorOverride,
  },
);

export const updateImageAction = createAsyncThunk(
  'updateImage/post',
  async (
    { file, oldfileId, onProgress }: { file: File; oldfileId: number; onProgress?: (perc: number) => void },
    { getState, dispatch },
  ) => {
    const session = (getState() as RootState).session;
    try {
      if (!session) throw new CustomError({ errorCode: 'SessionNotFound' });
      const { fileId, fileUrl } = await ImageManager.uploadImage(file, { ...getSessionIdentifier(session), onProgress });
      dispatch(deleteFileId(oldfileId));
      dispatch(addFileId(fileId));
      dispatch(updateFileDownloadUrls({ [fileId]: fileUrl }));
      return fileId;
    } catch (err) {
      logger.reportError(err, { message: 'Error in updateImageAction' });
      throw err;
    }
  },
  {
    serializeError: miniSerializeErrorOverride,
  },
);

export const scheduleFileFetch = createAsyncThunk(
  'fetchFileUrl/post',
  async ({ fileIds, expiry }: { fileIds: TFileId[]; expiry: number }, { getState, dispatch }) => {
    const session = (getState() as RootState).session;
    try {
      if (!session) throw new CustomError({ errorCode: 'SessionNotFound' });
      dispatch(updateExpiry(expiry));
      ImageManager.fetchFileUrl({ ...getSessionIdentifier(session), fileIds, expiry }, urlMap => {
        dispatch(updateFileDownloadUrls(urlMap));
      });
    } catch (err) {
      logger.reportError(err, { message: 'Error in scheduleFileFetch' });
      eventBus.emit('error/showToast', err);
      throw err;
    }
  },
);

export const uploadImage = createAsyncThunk(
  'uploadImageInitiate/post',
  async (
    {
      imageData,
      file,
      cb,
      onProgress,
    }: {
      file: File;
      onProgress?: (perc: number) => void;
      imageData: TImageElementConfig['data'];
      cb?: (fileId: number) => void;
    },
    { dispatch },
  ) => {
    let fileId;
    try {
      if (imageData?.fileId) {
        fileId = await dispatch(
          updateImageAction({
            file,
            oldfileId: imageData.fileId,
            onProgress,
          }),
        ).unwrap();
      } else {
        fileId = await dispatch(
          uploadImageAction({
            file,
            onProgress,
          }),
        ).unwrap();
      }
      cb && cb(fileId!);
    } catch (err) {
      logger.reportError(err, { message: 'Error in uploadImage' });
      eventBus.emit('error/showToast', err);
      throw err;
    }
  },
  {
    serializeError: miniSerializeErrorOverride,
  },
);

type TInitialState = {
  fileDownloadUrls: TfileIdToUrlMap;
  fileIds: number[];
  expiry: number | null;
};
const initialState: TInitialState = {
  fileDownloadUrls: {},
  fileIds: [],
  expiry: null,
};

const fileDataSlice = createSlice({
  name: 'fileStore',
  initialState,
  reducers: {
    updateFileDownloadUrls: (state, action: PayloadAction<TfileIdToUrlMap>) => {
      state.fileDownloadUrls = { ...state.fileDownloadUrls, ...action.payload };
      return state;
    },
    updateFileIds: (state, action: PayloadAction<number[]>) => {
      state.fileIds = action.payload;
      return state;
    },
    deleteFileId: (state, action: PayloadAction<number>) => {
      state.fileIds = state.fileIds.filter(id => id !== action.payload);
      return state;
    },
    addFileId: (state, action: PayloadAction<number>) => {
      state.fileIds.push(action.payload);
      return state;
    },
    updateExpiry: (state, action: PayloadAction<number>) => {
      state.expiry = action.payload;
      return state;
    },
  },
});

export const { updateFileDownloadUrls, updateFileIds, deleteFileId, addFileId, updateExpiry } = fileDataSlice.actions;
export default fileDataSlice.reducer;

export const fileIdsSelector = (state: RootState) => state.fileStore.fileIds;

export const fileDownloadUrlsSelector = (state: RootState) => state.fileStore.fileDownloadUrls;

export const fileUrlSelector = createSelector(
  fileDownloadUrlsSelector,
  (_: RootState, fileId: number) => fileId,
  (fileDownloadUrls, fileId) => fileDownloadUrls[fileId],
);
